import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { afterBlockingWindow, POINTER_EVENTS } from './lib/after-blocking-window';

// DEV-11302: defer widget loading until after the window load event so the
// ~0.5MB widget bundle doesn't compete with the page's own data.
// DEV-12444: requestIdleCallback({ timeout: 2000 }) fired inside the 3.5s
// bench settle and parsed Beacon during measured long-task time. Match the
// GA deferral in index.html: first interaction, or 5s after load.
function runWhenIdle(cb: () => void): void {
  const schedule = () => {
    afterBlockingWindow(cb, 5000, POINTER_EVENTS);
  };
  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
  }
}

// LunarCrush support chat ("Sophie") + Beacon notification banners.
//
// One drop-in script (DEV-10824) renders BOTH surfaces inside a single
// Shadow DOM host: the chat bubble (bottom-right) and marketing banners
// (full-width, top). DEV-10948 needs banners on EVERY route while the chat
// stays on the support-adjacent ones (/pricing, /support, /enterprise) —
// so "widget loaded" and "chat visible" are split: the script loads on
// every route and the host is never hidden; route changes toggle only the
// chat UI nodes inside the open shadow root.
//
// Chat UI nodes = the surfaces the script anchors to the bottom-right edge:
// launcher (.btn), unread dot (.badge), chat panel (.panel) and the voice-
// call surfaces (.call-ring/.callbar). The gate keys on those class names —
// viewport-independent, unlike the launcher's right offset (22px desktop /
// 16px mobile), which an offset-based gate missed on mobile, leaving Sophie
// visible on non-support pages (DEV-10957). Banners and notifications
// (.nf-*, .notif-teaser) use distinct classes and are left untouched. A
// MutationObserver re-applies the gate when the widget re-renders. If Joe
// ships a banners-only flag on widget.js, this can be replaced by it.
//
// The same widget runs in the webapp (apps/app) and on lunarcrush.com.

// Defaults to the prod Beacon widget; override with VITE_BEACON_WIDGET_SRC in
// .env.local to point at a local Beacon (e.g. for QA-ing banner behavior).
const WIDGET_SRC =
  import.meta.env.VITE_BEACON_WIDGET_SRC || 'https://beacon-ebon-nu.vercel.app/widget.js';
// The widget host is an unmarked <div> appended to <body>; its inline
// z-index is the one stable handle the script gives us (see widget.js).
const HOST_Z_INDEX = '2147483000';
const GREETING = "Hi! I'm Sophie. Questions about LunarCrush?";
// The chat UI nodes (and nothing in the notification surfaces). Matching by
// class works on every viewport; the launcher's right offset does not
// (22px desktop / 16px mobile). Notification nodes are `.nf-*`/`.notif-*`.
const CHAT_NODE_SELECTOR = '.btn, .badge, .panel, .call-ring, .callbar';
// The full-width notification bar that anchors to the TOP of the viewport
// (DEV-10948). Only this surface pushes page content down; toasts/teasers
// (.notif-teaser) and the chat nodes use other anchors and never offset.
const BANNER_SELECTOR = '.nf-banner';

// Pricing, the FAQ index (/support), FAQ articles (/support/:slug), and the
// Enterprise page (DEV-10820 — "Get pricing" opens the chat instead of email).
const ALLOWED = /^\/(pricing|support|enterprise)(\/|$)/;

let chatDesired = false;
let observerBound = false;
let readyBound = false;
// Push-down plumbing (DEV-10948): one resize listener + one ResizeObserver,
// shared across mounts, keep --lc-banner-offset in sync with the live bar.
let layoutBound = false;
let bannerRO: ResizeObserver | null = null;
// DEV-11309: suppress widget errors from flooding the console.
let errorHandlerBound = false;

function findWidgetHost(): HTMLElement | null {
  for (const el of Array.from(document.body.children)) {
    if (el instanceof HTMLElement && el.style.zIndex === HOST_Z_INDEX && el.shadowRoot) {
      return el;
    }
  }
  return null;
}

function applyChatVisibility() {
  const root = findWidgetHost()?.shadowRoot;
  if (!root) return;
  // Match the chat UI nodes by class and override visibility with an inline
  // !important (the widget styles them from a <style> sheet in the shadow
  // root). Selecting by class is viewport-agnostic — unlike the launcher's
  // right offset — and never touches the notification surfaces.
  for (const el of Array.from(root.querySelectorAll<HTMLElement>(CHAT_NODE_SELECTOR))) {
    if (chatDesired) el.style.removeProperty('visibility');
    else el.style.setProperty('visibility', 'hidden', 'important');
  }
}

// DEV-10948: when the Beacon banner is showing, the page must drop below it so
// the fixed top bar never covers content. We can't change the external
// widget's markup, so we measure the live bar and publish its height as the
// `--lc-banner-offset` CSS variable on <html>; the stylesheet consumes it
// (body padding + the .sticky-nav top offset). 0px when no banner — so the
// variable is a no-op the rest of the time.
function applyBannerOffset() {
  const root = findWidgetHost()?.shadowRoot;
  let offset = 0;
  let banner: HTMLElement | null = null;
  if (root) {
    for (const el of Array.from(root.querySelectorAll<HTMLElement>(BANNER_SELECTOR))) {
      const rect = el.getBoundingClientRect();
      // Only a visible, top-anchored, (near-)full-width bar offsets the page.
      // Guards against a hidden/dismissed banner (0 height), centered toasts,
      // and any future bottom-anchored notification reusing the class.
      const topBar = rect.height > 0 && rect.top <= 4 && rect.width > window.innerWidth * 0.6;
      if (topBar && rect.bottom > offset) {
        offset = rect.bottom;
        banner = el;
      }
    }
  }
  document.documentElement.style.setProperty('--lc-banner-offset', `${Math.max(0, Math.round(offset))}px`);
  // The bar's height changes when its copy wraps on a narrow viewport, which
  // does NOT trip the shadow MutationObserver — track the live node directly.
  if (banner && bannerRO) {
    bannerRO.disconnect();
    bannerRO.observe(banner);
  }
}

// Chat visibility + banner offset always re-evaluate together on any widget
// DOM change, so the gate and the push-down never drift apart.
function applyWidgetLayout() {
  applyChatVisibility();
  applyBannerOffset();
}

function bindWidgetObserver() {
  const root = findWidgetHost()?.shadowRoot;
  if (!root || observerBound) {
    applyWidgetLayout();
    return;
  }
  observerBound = true;
  new MutationObserver(applyWidgetLayout).observe(root, { childList: true, subtree: true });
  applyWidgetLayout();
}

function watchForHost() {
  // `lunarchat:ready` can fire before the host/UI nodes exist (and the gate
  // has nothing to bind to), so poll briefly until the host shows up — the
  // shadow MutationObserver takes over from there.
  if (observerBound) return;
  let ticks = 0;
  const timer = setInterval(() => {
    ticks += 1;
    if (findWidgetHost()) bindWidgetObserver();
    if (observerBound || ticks > 60) clearInterval(timer);
  }, 250);
}

// DEV-11309: the Beacon widget (widget.js) makes network calls to its own
// backend (signal/track/notify endpoints) that can fail transiently or when
// ad-blockers interfere. Those errors flood the console (35+ per session) and
// obscure real regressions during QA. We suppress them here so app devtools
// stay clean; the Beacon repo owns fixing the root cause inside widget.js.
const BEACON_DOMAIN = 'beacon-ebon-nu.vercel.app';
const BEACON_TRACK_URL = `https://${BEACON_DOMAIN}/api/chat/track`;

let trackFetchDedupeBound = false;
let trackFetchDedupeOriginal: typeof window.fetch | null = null;
const inFlightTrackPosts = new Map<string, Promise<Response>>();

async function bodyKey(body: BodyInit | null | undefined): Promise<string | null> {
  if (body == null) return '';
  if (typeof body === 'string') return body;
  if (body instanceof URLSearchParams) return body.toString();
  if (body instanceof Blob) return body.text();
  if (body instanceof ArrayBuffer) return new Uint8Array(body).join(',');
  if (ArrayBuffer.isView(body)) {
    return new Uint8Array(body.buffer, body.byteOffset, body.byteLength).join(',');
  }
  return null;
}

async function beaconTrackKey(input: RequestInfo | URL, init?: RequestInit): Promise<string | null> {
  const url = typeof input === 'string' || input instanceof URL ? String(input) : input.url;
  if (url !== BEACON_TRACK_URL) return null;

  const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
  if (method !== 'POST') return null;

  let keyBody = await bodyKey(init?.body);
  if (keyBody == null && init?.body == null && input instanceof Request) {
    try {
      keyBody = await input.clone().text();
    } catch {
      keyBody = null;
    }
  }
  if (keyBody == null) return null;
  return `${method} ${url} ${keyBody}`;
}

export function bindBeaconTrackDedupe() {
  if (trackFetchDedupeBound) return;
  trackFetchDedupeBound = true;
  trackFetchDedupeOriginal = window.fetch.bind(window);

  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    const originalFetch = trackFetchDedupeOriginal!;
    const key = await beaconTrackKey(input, init);
    if (!key) return originalFetch(input, init);

    const pending = inFlightTrackPosts.get(key);
    if (pending) {
      return pending.then((response) => {
        try {
          return response.clone();
        } catch {
          return response;
        }
      });
    }

    const request = originalFetch(input, init).finally(() => {
      inFlightTrackPosts.delete(key);
    });
    inFlightTrackPosts.set(key, request);
    return request;
  }) as typeof window.fetch;
}

function isBeaconError(event: ErrorEvent | PromiseRejectionEvent): boolean {
  // Script errors from the widget domain.
  if (event instanceof ErrorEvent) {
    const filename = event.filename ?? '';
    if (filename.includes(BEACON_DOMAIN)) return true;
    // Cross-origin script errors show as "Script error." with no filename.
    // If message is "Script error." and we have the widget loaded, suppress.
    if (event.message === 'Script error.' && findWidgetHost()) return true;
  }
  // Unhandled promise rejections from fetch calls inside the widget.
  if ('reason' in event) {
    const reason = event.reason;
    // Network errors (TypeError: Failed to fetch) from the widget's backend.
    const msg = reason?.message ?? String(reason ?? '');
    const stack = reason?.stack ?? '';
    if (stack.includes(BEACON_DOMAIN) || msg.includes(BEACON_DOMAIN)) return true;
  }
  return false;
}

function bindErrorSuppression() {
  if (errorHandlerBound) return;
  errorHandlerBound = true;
  // Suppress uncaught errors/rejections from the widget domain.
  window.addEventListener(
    'error',
    (e) => {
      if (isBeaconError(e)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    true
  );
  window.addEventListener(
    'unhandledrejection',
    (e) => {
      if (isBeaconError(e)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    true
  );
}

function ensureWidgetLoaded() {
  // DEV-11309: bind error suppression early, before the widget script loads.
  bindErrorSuppression();
  bindBeaconTrackDedupe();
  if (!readyBound) {
    readyBound = true;
    window.addEventListener('lunarchat:ready', bindWidgetObserver);
  }
  if (!layoutBound) {
    layoutBound = true;
    if (typeof ResizeObserver !== 'undefined') bannerRO = new ResizeObserver(applyBannerOffset);
    // Viewport width changes can reflow the bar (and so its height); recheck.
    window.addEventListener('resize', applyBannerOffset);
  }
  if (findWidgetHost()) {
    bindWidgetObserver();
    return;
  }
  watchForHost();
  if (document.querySelector('script[data-lc-chat]')) return; // injection in flight
  const s = document.createElement('script');
  s.src = WIDGET_SRC;
  s.defer = true;
  s.dataset.lcChat = '';
  s.setAttribute('data-greeting', GREETING);
  // DEV-11309: silently ignore script load failures (network, ad-blocker).
  s.onerror = () => {};
  document.body.appendChild(s);
}

const SupportChatWidget: React.FC = () => {
  const { pathname } = useLocation();
  const allowed = ALLOWED.test(pathname);

  // Widget loads once, on every route — banners render site-wide.
  // DEV-11302: defer to after load + idle so it doesn't compete with page data.
  useEffect(() => {
    runWhenIdle(ensureWidgetLoaded);
  }, []);

  // Route changes only gate the chat UI, never the banner host.
  useEffect(() => {
    chatDesired = allowed;
    applyChatVisibility();
  }, [allowed]);

  return null;
};

export default SupportChatWidget;
